'use client'

import React from 'react'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { Delete } from 'lucide-react'
import { signIn } from 'next-auth/react'
import { toast } from 'sonner'
import { getLocalRegister, validatePin } from '@/lib/actions'
import { useIsMedium, useModals, useUser } from '@/lib/hooks'
import { cn } from '@/lib/utils'

export const PinKeypad = () => {
  const router = useRouter()
  const { openModal } = useModals()
  const isTablet = useIsMedium()
  const { userPin, setUserToken, setUserPin } = useUser()
  const [isInvalid, setIsInvalid] = React.useState(false)
  const [isValidating, setIsValidating] = React.useState(false)
  const isValidatingRef = React.useRef(false)
  const handleValidationRef = React.useRef<(() => Promise<void>) | null>(null)

  const variants = {
    animateKeyPad: {
      translateX: isTablet ? '-52px' : '0',
    },
  }

  const keyArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'x']

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleClick = React.useCallback(
    (key: string | number | null) => {
      if (key === null || isValidating || (userPin.length >= 4 && key !== 'x'))
        return

      if (isInvalid) {
        setIsInvalid(false)
        setUserPin([])
        return
      }

      if (key === 'x') {
        setUserPin(userPin.slice(0, -1))
      } else if (userPin.length < 4) {
        setUserPin([...userPin, key.toString()])
      }
    },
    [userPin, isInvalid, isValidating],
  )

  React.useEffect(() => {
    handleValidationRef.current = async () => {
      if (isValidatingRef.current) return
      isValidatingRef.current = true
      setIsValidating(true)

      let isValid = false
      let token = null

      try {
        const res = await validatePin(userPin.join(''))
        isValid = res.isValid
        token = res.token
      } catch (error) {
        toast.error('An error occurred during PIN validation')
        isValidatingRef.current = false
        setIsValidating(false)
        return
      }

      if (!isValid) {
        toast.error('Invalid PIN')
        setIsInvalid(true)
        isValidatingRef.current = false
        setIsValidating(false)
        setTimeout(() => {
          setUserPin([])
          setIsInvalid(false)
        }, 0)
        return
      }

      let checkLocalRegister: any

      try {
        checkLocalRegister = await getLocalRegister()
      } catch (error) {
        toast.error('An error occurred while checking local registration')
        isValidatingRef.current = false
        setIsValidating(false)
        return
      }

      setUserToken(token)

      if (checkLocalRegister?.id) {
        await signIn('credentials', {
          pin: userPin,
          token,
          redirect: false,
        }).then(async (res: any) => {
          if (!res.ok) {
            toast.error(res?.error || 'Something went wrong')
            isValidatingRef.current = false
            setIsValidating(false)
          } else {
            toast.success('Logged in successfully')
            setUserPin([])
            isValidatingRef.current = false
            setIsValidating(false)
            router.push('/')
          }
        })
      } else {
        isValidatingRef.current = false
        setIsValidating(false)
        openModal('OPEN_REGISTER')
      }
    }
  })

  React.useEffect(() => {
    if (userPin.length === 4 && handleValidationRef.current) {
      handleValidationRef.current()
    }
  }, [userPin.length])

  // Handle keyboard input
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field or textarea
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Handle numeric keys 0-9
      if (/^[0-9]$/.test(event.key)) {
        handleClick(Number.parseInt(event.key))
      }
      // Handle backspace for deletion
      else if (event.key === 'Backspace') {
        handleClick('x')
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleClick])

  // Clear PIN on component mount and unmount for security
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
    setUserPin([])

    return () => {
      setUserPin([])
    }
  }, [])

  return (
    <>
      <motion.div
        className="flex w-full shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white p-8 shadow-card lg:w-[calc(50vw)] lg:translate-x-[100%] lg:p-10 dark:border-gray-700"
        variants={variants}
        animate="animateKeyPad"
      >
        <div className="flex w-full flex-col items-center gap-6 lg:gap-12">
          <h2 className="font-medium">Enter your PIN</h2>
          <div className="flex flex-col items-center gap-12">
            <div className="indicator flex animate-pin-invalid justify-center gap-2">
              {Array(4)
                .fill(null)
                .map((_, index) => (
                  <span
                    key={index}
                    className={cn(
                      'size-3 rounded-full bg-gray-200 transition-all',
                      {
                        'bg-black': userPin.length >= index + 1,
                        'scale-75 animate-pin-invalid bg-red-500': isInvalid,
                      },
                    )}
                  />
                ))}
            </div>

            <div className="keypad grid grid-cols-3 gap-3">
              {keyArray?.map((key) => (
                <button
                  key={key}
                  type="button"
                  disabled={isValidating || key === null}
                  className={cn(
                    'flex size-[72px] items-center justify-center rounded-lg bg-gray-100 font-mono text-xl font-bold transition-colors active:bg-gray-300 hover:bg-gray-200 lg:size-24',
                    {
                      'bg-transparent': key === null || key === 'x',
                      'pointer-events-none': key === null,
                      'cursor-not-allowed opacity-50': isValidating,
                    },
                  )}
                  onClick={() => handleClick(key)}
                >
                  {key === 'x' ? <Delete strokeWidth={1} size={36} /> : key}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  )
}
